<template>
  <!-- Public standalone pages (e.g. customer tracker) render without the
       admin shell — no sidebar/header (US #18826, task 18978). -->
  <div v-if="isPublicRoute" class="h-screen w-full overflow-y-auto">
    <router-view />
  </div>
  <!-- AB#21842 — a bare page (the Ops Command Center wall): no shell around it. -->
  <router-view v-else-if="route.meta.bare" />
  <div v-else class="app-root h-screen w-full flex flex-col">
    <ImpersonationBanner />
    <div
      class="main flex-1 min-h-0 w-full bg-app flex overflow-y-auto overflow-x-visible overflow-visible"
    >
      <Loader :loading="globalLoader" />
      <!-- Sidebar -->
      <Transition name="sidebar-slide overflow-visible">
        <Sidebar
          :navigation="navigation"
          class="transition-all duration-200 h-full overflow-visible"
          :class="
            mobileDevice
              ? ''
              : sidebarOpen
                ? isTablet
                  ? 'w-[232px] rail--float'
                  : 'w-[232px]'
                : 'w-[64px]'
          "
        />
      </Transition>
      <!-- AB#22137 — on a tablet the open rail floats over the page; a tap
           beside it closes it. -->
      <div
        v-if="isTablet && sidebarOpen && !mobileDevice"
        class="rail-scrim"
        data-test="rail-scrim"
        aria-hidden="true"
        @click="sidebarOpen = false"
      ></div>
      <div
        class="flex flex-col border-1 flex-1 h-full min-h-0 overflow-visible scroll-hidden"
        :class="{ mobileDevice: mobileDevice, desktop: !mobileDevice }"
        :style="
          mobileDevice
            ? { width: '100vw' }
            : { width: sidebarOpen && !isTablet ? 'calc(100vw - 232px)' : 'calc(100vw - 64px)' }
        "
      >
        <Header />
        <div
          class="h-full flex-1 w-full mx-auto heightClass overflow-y-auto min-h-0 overflow-visible"
        >
          <router-view />
        </div>
      </div>
    </div>
    <!-- AB#21684 — the portal's own Ask Atlas launcher; Zendesk's bubble is hidden. -->
    <AtlasLauncher />
    <!-- AB#22138 — the invitation to turn on notifications on this device. -->
    <PushPrompt />
    <!-- AB#22138 — the Notifications sheet (account menu → Notifications). -->
    <NotificationSettingsDrawer v-if="pushSettingsOpen" />
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import Header from './components/Header.vue';
import ImpersonationBanner from './components/ImpersonationBanner.vue';
import Sidebar from './components/Sidebar.vue';
import { useMainStore } from './store';
import Loader from './components/Shared/Loader.vue';
import { getImpersonation } from './composables/impersonation';
import { startZendeskWidget } from './composables/useZendeskWidget';
import AtlasLauncher from '@/components/Shared/AtlasLauncher.vue';
import PushPrompt from '@/components/Notifications/PushPrompt.vue';
import NotificationSettingsDrawer from '@/components/Notifications/NotificationSettingsDrawer.vue';
import { usePushNotifications } from '@/composables/usePushNotifications';
import { startAppInstallTracking } from '@/composables/useAppInstallTracking';
import { useTablet } from '@/composables/useTablet';

const store = useMainStore();
const route = useRoute();
const { navigation, mobileDevice, sidebarOpen, globalLoader, user, role } = storeToRefs(store);

/** Public/standalone routes render without the admin sidebar + header. */
const isPublicRoute = computed<boolean>(() => route.meta?.public === true);
const isTablet = useTablet();
const { settingsOpen: pushSettingsOpen } = usePushNotifications();
// AB#22137 — on a tablet the open rail covers the page, so going somewhere
// puts it away again.
watch(
  () => route.fullPath,
  () => {
    if (isTablet.value) sidebarOpen.value = false;
  }
);
const screenWidth = ref(window.innerWidth);
const screenHeight = ref(window.innerHeight);

const updateScreenSize = () => {
  screenWidth.value = window.innerWidth;
  screenHeight.value = window.innerHeight;
  mobileDevice.value = window.innerWidth <= 767;
};

/**
 * Support widget (AB#21165).
 *
 * Started from the shell rather than a page so it is there wherever the user
 * happens to be, and started from a watcher rather than onMounted because the
 * shell mounts before `authuser` has answered — the whole point is that the
 * contact form already knows who is asking. First user wins; `startZendeskWidget`
 * returns false when there is no key configured.
 *
 * In an impersonated session the ticket is attributed to the real admin, not the
 * account being viewed through — `getImpersonation()` is who that is.
 */
let zendeskStarted = false;
watch(
  user,
  (value) => {
    if (zendeskStarted || !value) return;
    zendeskStarted = startZendeskWidget(value, role.value, getImpersonation()?.by);
  },
  { immediate: true }
);

// Installed-app tracking: once someone is signed in, an open as the installed
// app is recorded for their device (useAppInstallTracking).
watch(
  user,
  (value) => {
    if (value) startAppInstallTracking();
  },
  { immediate: true }
);

onMounted(() => {
  updateScreenSize();
  window.addEventListener('resize', updateScreenSize);
});

onUnmounted(() => {
  window.removeEventListener('resize', updateScreenSize);
});
</script>

<style lang="scss">
.heightClass {
  height: calc(100vh - 53px);
}
/*
  AB#22137 — on a phone, 100vh is the height with the browser's address and
  tool bars hidden, so a page sized to it runs under them and its last rows
  can never be scrolled into view (iPhone Chrome and Safari). The shell takes
  the visible height instead (100dvh) and the page area takes what is left
  under the app bar.
*/
@media (max-width: 767.98px) {
  .app-root {
    height: 100vh;
    height: 100dvh;
  }
  .app-root .heightClass {
    height: auto;
    flex: 1 1 auto;
    min-height: 0;
    /* Room for the Ask Atlas launcher floating above the bottom bar, so the
       last row of a page can be scrolled clear of it. */
    padding-bottom: 72px;
  }
}
/*
  AB#22137 — a tablet browser hides and shows its bars like a phone's, so the
  shell takes the visible height here too.
*/
@media (min-width: 768px) and (max-width: 1024px) {
  .app-root {
    height: 100vh;
    height: 100dvh;
  }
  .app-root .heightClass {
    height: auto;
    flex: 1 1 auto;
    min-height: 0;
  }
  .rail-scrim {
    position: fixed;
    inset: 0;
    z-index: 10026;
    background: rgba(12, 14, 18, 0.32);
  }
}
.desktop {
  width: 100%;
}
.mobileDevice {
  width: 100vw;
  /* AB#21537 — room for the bottom navigation bar on a phone. */
  padding-bottom: calc(60px + env(safe-area-inset-bottom, 0px));
}
.spinner {
  border: 5px solid var(--accent);
  border-top-color: transparent;
  animation: spin 1s linear infinite;

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }
}
.sidebar-slide-enter-active,
.sidebar-slide-leave-active {
  transition:
    opacity 0.3s,
    transform 0.3s;
}
.sidebar-slide-enter-from,
.sidebar-slide-leave-to {
  opacity: 0;
  transform: translateX(-100%);
}
.sidebar-slide-enter-to,
.sidebar-slide-leave-from {
  opacity: 1;
  transform: translateX(0);
}
</style>
